import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Download, Inbox, Loader2, Store, Warehouse, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { SheetPanel } from "./SheetPanel";
import {
    SC_LONG_TIMEOUT_MS, STATUS_TONE, blobErrorMessage, fmt, fmtDateTime, fmtDay, fmtMoney, fmtPeriod, labelOf, scMessage,
} from "./scUtils";

const B = OPS.sc.board;
const ALL = "all";
const STATUS_KEYS = ["not_started", "draft", "submitted", "approved"];
const chip = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold";

/** «تصدير Excel»: a blob download (as RecordsView.ExportButton); a refused
 *  export carries its JSON error inside the blob, read with blobErrorMessage. */
export function ExportButton({ period }) {
    const { t, lang } = useI18n();
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const r = await api.get("/sc/export", { params: { period, lang }, responseType: "blob", timeout: SC_LONG_TIMEOUT_MS });
            const name = (r.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `stock_count_${period}.xlsx`;
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = name;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
            toast.success(t("stk_board_exported"));
        } catch (e) {
            toast.error((await blobErrorMessage(e, lang)) || t("stk_board_export_failed"));
        } finally {
            setBusy(false);
        }
    };
    return (
        <button type="button" data-testid={B.exportBtn} onClick={run} disabled={busy || !period}
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[color:var(--brand-brown)] px-4 text-xs font-bold text-white shadow-[var(--shadow-sm)] transition-[opacity,transform] duration-150 active:scale-[0.97] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {busy ? t("stk_board_exporting") : t("stk_board_export")}
        </button>
    );
}

/** Six tiles: locations, the four statuses, and the value (when values are visible). */
function Totals({ totals, scope, showValues, loading }) {
    const { t, lang } = useI18n();
    const num = (v) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(v || 0);
    const statuses = scope?.labels?.statuses;
    const tiles = [
        { k: "locations", label: t("stk_board_total_locations"), value: num(totals.locations), raw: totals.locations ?? 0 },
        ...STATUS_KEYS.map((k) => ({ k, label: labelOf(statuses, k, lang), value: num(totals[k]), raw: totals[k] ?? 0 })),
    ];
    if (showValues) {
        tiles.push({ k: "value", label: t("stk_board_total_value"), value: fmtMoney(totals.value || 0, lang), raw: totals.value ?? 0,
                     accent: true, ltr: true });
    }
    return (
        <section data-testid={B.totals} data-loading={loading ? "true" : "false"}
                 className={`grid grid-cols-3 gap-2 transition-opacity duration-200 sm:grid-cols-6 ${loading ? "opacity-60" : "opacity-100"}`}>
            {tiles.map((tile) => (
                <div key={tile.k}
                     className={`min-w-0 rounded-[var(--radius-md)] border px-2.5 py-2 ${tile.accent
                         // a riyal total does not fit a third of 390 px: full width, first, on phones
                         ? "order-first col-span-3 border-[color:var(--brand-orange)]/40 bg-[color:var(--brand-orange-soft)] sm:order-none sm:col-span-1"
                         : "border-[color:var(--border-soft)] bg-[color:var(--bg-surface)]"}`}>
                    <p className="truncate text-[10px] font-bold leading-tight text-[color:var(--ink-muted)]">{tile.label}</p>
                    <p data-testid={B.total(tile.k)} data-value={tile.raw}
                       className="mt-1 truncate text-sm font-extrabold text-[color:var(--ink)] sm:text-base">
                        <bdi dir="ltr">{tile.value}</bdi>
                    </p>
                </div>
            ))}
        </section>
    );
}

function BoardRow({ row, scope, showValues, onOpen }) {
    const { t, tn, lang, dir } = useI18n();
    const loc = row.location || {};
    const id = loc.id;
    const n = (v) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(v || 0);
    const Chevron = dir === "rtl" ? ChevronLeft : ChevronRight;
    const KindIcon = loc.kind === "warehouse" ? Warehouse : Store;
    return (
        <li>
            <button type="button" data-testid={B.row(id)} data-status={row.status} onClick={() => onOpen(id)}
                    className="group flex w-full items-center gap-2 px-1 py-3 text-start transition-[background-color] duration-150 hover:bg-[color:var(--brand-orange-soft)]">
                <span className="block min-w-0 flex-1 space-y-1">
                    <span className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                        <span className="flex min-w-0 items-center gap-1.5 text-sm font-bold text-[color:var(--ink)]">
                            <KindIcon className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                            <span data-testid={B.cell(id, "name")} className="truncate">{tn(loc)}</span>
                            {loc.is_active === false ? (
                                <span className={`${chip} bg-[color:var(--bg-surface-2)] text-[color:var(--ink-faint)]`}>{t("stk_board_inactive")}</span>
                            ) : null}
                        </span>
                        <span className="flex shrink-0 items-center gap-1">
                            {row.late ? (
                                <span data-testid={B.cell(id, "late")} className={`${chip} bg-[color:var(--danger-soft)] text-[color:var(--danger-soft-fg)]`}>
                                    {t("stk_board_late")}
                                </span>
                            ) : null}
                            <span data-testid={B.cell(id, "status")} data-status={row.status} className={`${chip} ${STATUS_TONE[row.status] || ""}`}>
                                {labelOf(scope?.labels?.statuses, row.status, lang)}
                            </span>
                        </span>
                    </span>
                    <span className="flex flex-wrap items-baseline justify-between gap-x-3 text-[11px] text-[color:var(--ink-muted)]">
                        <span data-testid={B.cell(id, "progress")}>
                            <bdi dir="ltr" className="font-bold text-[color:var(--ink)]">{n(row.counted_n)}/{n(row.items_n)}</bdi> {t("stk_board_items_suffix")}
                        </span>
                        {showValues ? (
                            <span data-testid={B.cell(id, "value")} data-value={row.value ?? ""} className="text-xs font-bold text-[color:var(--ink)]">
                                {row.value === null || row.value === undefined ? "—" : <bdi dir="ltr">{fmtMoney(row.value, lang)}</bdi>}
                            </span>
                        ) : null}
                    </span>
                    <span data-testid={B.cell(id, "updated")} className="block text-[11px] text-[color:var(--ink-faint)]">
                        {row.updated_at ? <>{t("stk_board_updated")} {fmtDateTime(row.updated_at, lang)}</> : t("stk_board_never")}
                        {row.open_until ? <> · {t("stk_board_open_until")} {fmtDay(row.open_until, lang)}</> : null}
                        {showValues && row.missing_cost_n ? <> · {fmt(t("stk_board_missing_cost"), { n: n(row.missing_cost_n) })}</> : null}
                    </span>
                </span>
                <Chevron className="h-4 w-4 shrink-0 text-[color:var(--ink-faint)]" />
            </button>
        </li>
    );
}

/** «لوحة الجرد»: one row per location in scope for a period — status, progress,
 *  value, «متأخر» — with the sheet panel (finance actions) and the Excel export. */
export function Board({ scope }) {
    const { t, lang } = useI18n();
    const periods = useMemo(() => scope?.periods || [], [scope]);
    const [period, setPeriod] = useState(scope?.default_period || periods[0] || "");
    const [kind, setKind] = useState(ALL);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [openId, setOpenId] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const showValues = !!scope?.caps?.can_see_values;

    useEffect(() => {
        if (!period) { setLoading(false); return undefined; }
        let alive = true;
        setLoading(true);
        setFailed(false);
        api.get("/sc/board", { params: { period } })
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => {
                if (!alive) return;
                // a failed switch to another month must not leave the old month's rows under the new label
                setData((d) => (d && d.period !== period ? null : d));
                const msg = scMessage(e, lang);
                if (msg) toast.error(msg); else setFailed(true);
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [period, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // the kind filter only makes sense when the scope holds both kinds
    const kinds = useMemo(() => [...new Set((scope?.locations || []).map((l) => l.kind))], [scope]);
    const rows = useMemo(
        () => (data?.rows || []).filter((r) => kind === ALL || r.location?.kind === kind),
        [data, kind],
    );
    const totals = useMemo(() => {
        if (kind === ALL && data?.totals) return data.totals;
        const out = { locations: rows.length, not_started: 0, draft: 0, submitted: 0, approved: 0, value: 0 };
        rows.forEach((r) => {
            out[r.status] = (out[r.status] || 0) + 1;
            if (typeof r.value === "number") out.value += r.value;
        });
        out.value = Math.round(out.value * 100) / 100;
        return out;
    }, [data, rows, kind]);
    const costSets = data?.cost_sets || [];

    return (
        <div data-testid={B.view} className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger data-testid={B.period} className="h-10 min-w-[9rem] flex-1 sm:w-48 sm:flex-none">
                        <CalendarDays className="h-4 w-4 text-[color:var(--brand-brown)]" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {periods.map((p) => (
                            <SelectItem key={p} value={p}>{fmtPeriod(p, lang)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {kinds.length > 1 ? (
                    <Select value={kind} onValueChange={setKind}>
                        <SelectTrigger data-testid={B.kind} className="h-10 min-w-[8rem] flex-1 sm:w-40 sm:flex-none">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL}>{t("stk_board_kind_all")}</SelectItem>
                            {(scope?.labels?.kinds || []).filter((k) => kinds.includes(k.key)).map((k) => (
                                <SelectItem key={k.key} value={k.key}>{labelOf(scope.labels.kinds, k.key, lang)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : null}
                <div className="ms-auto">
                    <ExportButton period={period} />
                </div>
                {data?.window?.start ? (
                    <p className="w-full text-[11px] text-[color:var(--ink-muted)]">
                        {t("stk_board_window")}: {fmtDay(data.window.start, lang)} – {fmtDay(data.window.end, lang)}
                        {showValues ? (
                            <> · {costSets.length
                                ? <>{t("stk_board_cost_sets")}: {costSets.map((s) => <bdi key={s.id} dir="ltr" className="me-1">{s.period}</bdi>)}</>
                                : <span className="font-semibold text-[color:var(--warning-soft-fg)]">{t("stk_board_no_cost_sets")}</span>}
                            </>
                        ) : null}
                    </p>
                ) : null}
            </div>

            <Totals totals={totals} scope={scope} showValues={showValues} loading={loading} />

            {failed ? (
                <div data-testid={OPS.sc.offlineError}
                     className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                        <WifiOff className="h-3.5 w-3.5" />{t("stk_admin_offline_error")}
                    </span>
                    <Button size="sm" variant="outline" data-testid={OPS.sc.retry} onClick={() => setReloadKey((k) => k + 1)}>
                        {t("stk_admin_retry")}
                    </Button>
                </div>
            ) : null}

            <section className="rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                {loading && !data ? <InlineLoader /> : failed && !data ? null : rows.length ? (
                    <ul className={`divide-y divide-[color:var(--border-soft)] transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
                        {rows.map((r) => (
                            <BoardRow key={r.location?.id} row={r} scope={scope} showValues={showValues} onOpen={setOpenId} />
                        ))}
                    </ul>
                ) : (
                    <p data-testid={B.empty} className="flex flex-col items-center gap-2 py-10 text-sm text-[color:var(--ink-muted)]">
                        <Inbox className="h-6 w-6 text-[color:var(--ink-faint)]" />{t("stk_board_empty")}
                    </p>
                )}
            </section>

            <SheetPanel locationId={openId} period={period} scope={scope} onClose={() => setOpenId(null)}
                        onChanged={() => setReloadKey((k) => k + 1)} />
        </div>
    );
}
